/**
 * decalBuilders — the groundMark grammar verb + school mark families.
 *
 * Fourth grammar surface of the FX buildout (spec:
 * `docs/plans/fx-grammar-specs/ground-marks-and-decals.md`). A ground mark is
 * a lingering, school-colored, PURELY COSMETIC stain where an ability landed —
 * scorch rings, frost patches, visual-only poison pools, holy sigils, shadow
 * stains. Marks are PASSIVE aftermath ("something HAPPENED here"), the
 * mirror-image of zones ("a mechanic is live HERE") — see spec §0.
 *
 * Deliberate design (spec §2.1 / §5.1):
 *  - `groundMark()` returns a **DecalSpec**, NOT a VFXPrimitive. Decals never
 *    enter the ShapeSpawner/VFXEffectPlayer pipeline: combat shape budgets,
 *    `sweepStale`'s 60s ceiling, and play-time palette semantics are burst
 *    concerns a 90s scorch must not inherit.
 *  - Marks are never registry citizens — no `VFXEffectConfig`, unreachable by
 *    `playById`. The only consumers are DecalLayer.place() / the
 *    `vfx:ground_mark` emit sites.
 *  - **Static-only doctrine** (spec §3): a mark is draw-ops + a fade
 *    lifecycle. No loops, no pulses, no tween keys anywhere in the spec —
 *    that vocabulary telegraphs LIVE mechanics and belongs to zones.
 *    Guarded by `pfDecalStatic.guard.test.ts` (PF-DECAL-STATIC).
 *  - **Never `style.glow`** — the glow trap reroutes into the bloom container
 *    inside the vfx layer (z=1000, above the player). Luminosity comes from
 *    sprite `blendMode: 'add'` inside the decal container instead.
 *
 * Draw programs use ONLY `fillPolygon` / `strokePolyline` / `fillRect` — the
 * exact surface the bake adapter implements (`PixiBakeGraphics.
 * makeRawGraphicsAdapter`), so every mark bakes to a cached texture.
 */

import { ABILITY_SCHOOL_COLORS } from '../../../shared/constants/AbilitySchoolColors';
import { lerpColor } from '../../../shared/utils/MathUtils';
import {
  tileCategoryStyle,
  biomeHasTileProfile,
} from '../../rendering/textures/BiomeTextureModule';
import { ServiceRegistry } from '../../core/ServiceRegistry';

// ── Families ─────────────────────────────────────────────────────────────

export type MarkFamily =
  | 'scorch'   // fire: charred ring, dark core + ember rim, darkening
  | 'frost'    // frost/ice: pale rime patch + shard flecks, lightening
  | 'pool'     // nature/poison: VISUAL-ONLY puddle, flat gloss ellipse
  | 'sigil'    // holy: faint gold circle-and-star inscription, additive-lite
  | 'stain'    // shadow: soft-edged black-violet blot, darkening
  | 'glyph'    // arcane: thin rune ring, additive-lite
  | 'scuff';   // physical: gray gouge scratches, darkening

export const MARK_FAMILIES: readonly MarkFamily[] = [
  'scorch', 'frost', 'pool', 'sigil', 'stain', 'glyph', 'scuff',
];

/** School → default mark family (auto-derivation SSOT, spec §4.3). Joins the
 *  school-identity drift guards: PF-DECAL-STATIC asserts this covers every
 *  reachable content school. */
export const SCHOOL_MARK_FAMILY: Record<string, MarkFamily> = {
  fire: 'scorch',
  frost: 'frost',
  ice: 'frost',
  nature: 'pool',
  poison: 'pool',
  holy: 'sigil',
  shadow: 'stain',
  arcane: 'glyph',
  physical: 'scuff',
};

// ── Constants (spec §2.1 / §3 / §5.2) ───────────────────────────────────

export const DECAL_DEFAULT_TTL_MS = 20_000;
/** Doctrine ceiling — aftermath always reads quieter than live zones. */
export const DECAL_ALPHA_CAP = 0.55;
/** Ground-perspective squash — the groundCloud/zoneRing ratio. */
export const DECAL_HEIGHT_RATIO = 0.42;
export const DECAL_DEFAULT_SIZE = 56;

// ── Spec ────────────────────────────────────────────────────────────────

export interface DecalSpec {
  family: MarkFamily;
  school: string;              // palette source (ABILITY_SCHOOL_COLORS)
  width: number;               // px; height derives width * DECAL_HEIGHT_RATIO
  ttlMs: number;               // hold time before fade-out begins
  colorCore: number;           // resolved AFTER biome blend (§2.3)
  colorEdge: number;           // ≥2 tones — palette-lock rule carries over
  alpha: number;               // peak alpha, CAPPED at DECAL_ALPHA_CAP
  blend: 'multiply' | 'normal' | 'add';   // per-family default (semantic;
                               // DecalLayer maps to the sprite substrate)
  seed: number;                // deterministic per-mark jitter
}

interface FamilyStyle {
  blend: DecalSpec['blend'];
  /** Biome ground-tint blend weight for colorCore (spec §2.3). */
  groundWeight: number;
  /** Peak alpha (pre-cap). */
  alpha: number;
  core: (school: number) => number;
  edge: (school: number) => number;
}

/**
 * Per-family style table. `multiply` families DARKEN whatever ground they sit
 * on (char/stain/gouge); `frost` lightens with a `normal` high-value tint;
 * `sigil`/`glyph` are inscribed light at low alpha via `add` — as sprite
 * blendMode, never `style.glow`.
 */
const FAMILY_STYLE: Record<MarkFamily, FamilyStyle> = {
  scorch: {
    blend: 'multiply', groundWeight: 0.35, alpha: 0.5,
    core: (s) => lerpColor(s, 0x000000, 0.55),
    edge: (s) => s,
  },
  frost: {
    blend: 'normal', groundWeight: 0.25, alpha: 0.5,
    core: (s) => lerpColor(s, 0xffffff, 0.45),
    edge: (s) => s,
  },
  pool: {
    blend: 'normal', groundWeight: 0.3, alpha: 0.5,
    core: (s) => lerpColor(s, 0x000000, 0.25),
    edge: (s) => lerpColor(s, 0xffffff, 0.2),
  },
  sigil: {
    blend: 'add', groundWeight: 0.2, alpha: 0.4,
    core: (s) => lerpColor(s, 0xffffff, 0.35),
    edge: (s) => s,
  },
  stain: {
    blend: 'multiply', groundWeight: 0.35, alpha: 0.55,
    core: (s) => lerpColor(s, 0x000000, 0.6),
    edge: (s) => lerpColor(s, 0x000000, 0.3),
  },
  glyph: {
    blend: 'add', groundWeight: 0.2, alpha: 0.4,
    core: (s) => lerpColor(s, 0xffffff, 0.3),
    edge: (s) => s,
  },
  scuff: {
    blend: 'multiply', groundWeight: 0.4, alpha: 0.45,
    core: (s) => lerpColor(s, 0x000000, 0.45),
    edge: (s) => lerpColor(s, 0xffffff, 0.15),
  },
};

/** Biome-aware core mix (spec §2.3) — exported pure for unit tests. On biomes
 *  without a tile profile, skip the blend and use the raw family core (same
 *  graceful degradation the tile renderer uses). */
export function resolveDecalCore(
  family: MarkFamily,
  schoolColor: number,
  biome: string | null | undefined,
): number {
  const style = FAMILY_STYLE[family];
  const base = style.core(schoolColor);
  if (!biome || !biomeHasTileProfile(biome)) return base;
  const ground = tileCategoryStyle(biome, 'ground').tint;
  return lerpColor(base, ground, style.groundWeight);
}

/** Live biome authority read: `MapSystem.getCurrentLevel()?.scene?.biome`
 *  (the resolveLayers/NatureLayer pattern). Null in headless/tests. */
function liveBiome(): string | null {
  try {
    const mapSystem = ServiceRegistry.getInstance().get('MapSystem', { optional: true }) as
      | { getCurrentLevel?: () => { scene?: { biome?: string | null } } | null }
      | undefined;
    return mapSystem?.getCurrentLevel?.()?.scene?.biome ?? null;
  } catch {
    return null;
  }
}

/** The one public verb (spec §2.1). Family defaulted from school,
 *  overridable; biome injected for tests, live-read otherwise. */
export function groundMark(opts: {
  school: string;
  size?: number;               // ?? DECAL_DEFAULT_SIZE — AOEs pass their radius
  ttlMs?: number;              // ?? DECAL_DEFAULT_TTL_MS
  family?: MarkFamily;         // ?? SCHOOL_MARK_FAMILY[school]
  biome?: string | null;       // ?? live scene.biome (injected for tests)
  seed?: number;               // deterministic jitter (tests); random otherwise
}): DecalSpec {
  const school = opts.school;
  const family = opts.family ?? SCHOOL_MARK_FAMILY[school] ?? 'scuff';
  const style = FAMILY_STYLE[family];
  const schoolColor = ABILITY_SCHOOL_COLORS[school] ?? ABILITY_SCHOOL_COLORS.physical;
  const biome = opts.biome !== undefined ? opts.biome : liveBiome();
  return {
    family,
    school,
    width: opts.size ?? DECAL_DEFAULT_SIZE,
    ttlMs: opts.ttlMs ?? DECAL_DEFAULT_TTL_MS,
    colorCore: resolveDecalCore(family, schoolColor, biome),
    colorEdge: style.edge(schoolColor),
    alpha: Math.min(style.alpha, DECAL_ALPHA_CAP),
    blend: style.blend,
    seed: opts.seed ?? ((Math.random() * 0xffffffff) >>> 0),
  };
}

// ── Draw programs (spec §2.1 — static ops only, bake-adapter surface) ───

/** The exact draw surface `PixiBakeGraphics.makeRawGraphicsAdapter`
 *  implements — restricting to it guarantees every family bakes. */
export interface DecalDrawTarget {
  fillPolygon(points: Array<{ x: number; y: number }>, color: number, alpha?: number): void;
  strokePolyline(points: Array<{ x: number; y: number }>, color: number, lineWidth: number, alpha?: number): void;
  fillRect(x: number, y: number, width: number, height: number, color: number, alpha?: number): void;
}

/** mulberry32 — deterministic per-seed RNG for shape jitter. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

type Pt = { x: number; y: number };

/** Jittered ground-perspective ellipse outline (centered at 0,0). */
function ellipsePoints(rx: number, ry: number, n: number, jitter: number, rng: () => number): Pt[] {
  const pts: Pt[] = [];
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    const j = 1 + (rng() - 0.5) * 2 * jitter;
    pts.push({ x: Math.cos(a) * rx * j, y: Math.sin(a) * ry * j });
  }
  return pts;
}

/** Arc segment along the ellipse perimeter (for broken rings). */
function arcPoints(rx: number, ry: number, a0: number, a1: number, n: number): Pt[] {
  const pts: Pt[] = [];
  for (let i = 0; i <= n; i++) {
    const a = a0 + ((a1 - a0) * i) / n;
    pts.push({ x: Math.cos(a) * rx, y: Math.sin(a) * ry });
  }
  return pts;
}

/**
 * Execute a mark's family draw program into `gfx`. Purely static draw ops —
 * zero tweens, zero loops (spec §3 doctrine). Deterministic under spec.seed.
 * Internal alphas are RELATIVE (≤1) — the sprite's peak alpha (spec.alpha,
 * capped) multiplies the whole texture.
 */
export function drawMark(gfx: DecalDrawTarget, spec: DecalSpec): void {
  const rng = mulberry32(spec.seed);
  const rx = spec.width / 2;
  const ry = rx * DECAL_HEIGHT_RATIO;
  const core = spec.colorCore;
  const edge = spec.colorEdge;

  switch (spec.family) {
    case 'scorch': {
      // Charred core blob + broken ember ring + radial char streaks.
      gfx.fillPolygon(ellipsePoints(rx * 0.55, ry * 0.55, 14, 0.25, rng), core, 1);
      const gaps = 5;
      for (let i = 0; i < gaps; i++) {
        const a0 = (i / gaps) * Math.PI * 2 + rng() * 0.3;
        const a1 = a0 + (Math.PI * 2 / gaps) * (0.55 + rng() * 0.2);
        gfx.strokePolyline(arcPoints(rx, ry, a0, a1, 6), edge, 2, 0.9);
      }
      for (let i = 0; i < 5; i++) {
        const a = rng() * Math.PI * 2;
        gfx.strokePolyline(
          [
            { x: Math.cos(a) * rx * 0.5, y: Math.sin(a) * ry * 0.5 },
            { x: Math.cos(a) * rx * (0.95 + rng() * 0.15), y: Math.sin(a) * ry * (0.95 + rng() * 0.15) },
          ],
          core, 2, 0.8,
        );
      }
      break;
    }
    case 'frost': {
      // Irregular rime patch + 3-4 static shard triangles (shatter litter).
      gfx.fillPolygon(ellipsePoints(rx * 0.9, ry * 0.9, 12, 0.3, rng), core, 0.9);
      const shards = 3 + Math.floor(rng() * 2);
      for (let i = 0; i < shards; i++) {
        const a = rng() * Math.PI * 2;
        const d = rng() * rx * 0.5;
        const cx = Math.cos(a) * d;
        const cy = Math.sin(a) * d * DECAL_HEIGHT_RATIO;
        const s = rx * (0.12 + rng() * 0.1);
        gfx.fillPolygon(
          [
            { x: cx, y: cy - s },
            { x: cx + s * 0.5, y: cy + s * 0.4 },
            { x: cx - s * 0.5, y: cy + s * 0.4 },
          ],
          edge, 1,
        );
      }
      break;
    }
    case 'pool': {
      // Two nested gloss ellipses — flat, VISUAL-ONLY puddle (spec §3.6).
      gfx.fillPolygon(ellipsePoints(rx, ry, 18, 0.12, rng), edge, 0.7);
      gfx.fillPolygon(ellipsePoints(rx * 0.62, ry * 0.62, 14, 0.15, rng), core, 1);
      // Gloss highlight sliver.
      gfx.fillPolygon(
        ellipsePoints(rx * 0.22, ry * 0.16, 8, 0.1, rng).map((p) => ({
          x: p.x - rx * 0.25, y: p.y - ry * 0.3,
        })),
        edge, 0.5,
      );
      break;
    }
    case 'sigil': {
      // Thin inscribed circle + star chords (holy inscription).
      const ring = arcPoints(rx, ry, 0, Math.PI * 2, 28);
      gfx.strokePolyline(ring, edge, 2, 0.95);
      const points = 4;
      for (let i = 0; i < points; i++) {
        const a0 = (i / points) * Math.PI * 2 + rng() * 0.1;
        const a1 = a0 + Math.PI * (0.75 + rng() * 0.1);
        gfx.strokePolyline(
          [
            { x: Math.cos(a0) * rx * 0.85, y: Math.sin(a0) * ry * 0.85 },
            { x: Math.cos(a1) * rx * 0.85, y: Math.sin(a1) * ry * 0.85 },
          ],
          core, 1.5, 0.85,
        );
      }
      break;
    }
    case 'stain': {
      // 2-3 overlapping soft blots, offset by seed.
      const blots = 2 + Math.floor(rng() * 2);
      for (let i = 0; i < blots; i++) {
        const ox = (rng() - 0.5) * rx * 0.7;
        const oy = (rng() - 0.5) * ry * 0.7;
        const scale = 0.55 + rng() * 0.35;
        const color = i === 0 ? core : edge;
        gfx.fillPolygon(
          ellipsePoints(rx * scale, ry * scale, 12, 0.28, rng).map((p) => ({
            x: p.x + ox, y: p.y + oy,
          })),
          color, i === 0 ? 1 : 0.7,
        );
      }
      break;
    }
    case 'glyph': {
      // Thin rune ring + tick marks + inner ring (arcane residue).
      gfx.strokePolyline(arcPoints(rx, ry, 0, Math.PI * 2, 28), edge, 1.5, 0.95);
      gfx.strokePolyline(arcPoints(rx * 0.6, ry * 0.6, 0, Math.PI * 2, 20), core, 1, 0.7);
      const ticks = 8;
      for (let i = 0; i < ticks; i++) {
        const a = (i / ticks) * Math.PI * 2 + rng() * 0.15;
        gfx.strokePolyline(
          [
            { x: Math.cos(a) * rx * 0.82, y: Math.sin(a) * ry * 0.82 },
            { x: Math.cos(a) * rx * 1.0, y: Math.sin(a) * ry * 1.0 },
          ],
          core, 1.5, 0.85,
        );
      }
      break;
    }
    case 'scuff': {
      // 3 short angled gouge lines + small chips (physical scrape).
      for (let i = 0; i < 3; i++) {
        const a = -0.5 + rng() * 1.0; // roughly-horizontal gouges
        const ox = (rng() - 0.5) * rx;
        const oy = (rng() - 0.5) * ry;
        const len = rx * (0.4 + rng() * 0.4);
        gfx.strokePolyline(
          [
            { x: ox - Math.cos(a) * len * 0.5, y: oy - Math.sin(a) * len * 0.5 * DECAL_HEIGHT_RATIO },
            { x: ox + Math.cos(a) * len * 0.5, y: oy + Math.sin(a) * len * 0.5 * DECAL_HEIGHT_RATIO },
          ],
          core, 2.5, 0.9,
        );
      }
      for (let i = 0; i < 3; i++) {
        const cx = (rng() - 0.5) * rx * 1.2;
        const cy = (rng() - 0.5) * ry * 1.2;
        gfx.fillRect(cx, cy, 2, 2, edge, 0.7);
      }
      break;
    }
  }
}
